// Camera capture: Capacitor on iOS/Android, getUserMedia on web. Returns a
// Blob ready to upload to Supabase Storage. NEVER reads from the gallery
// (CLAUDE.md hard rule #4).

import { Camera, CameraDirection, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';

export type FacingMode = 'user' | 'environment';

export async function captureCamera(facing: FacingMode): Promise<Blob> {
  if (Capacitor.isNativePlatform()) {
    const photo = await Camera.getPhoto({
      quality: 80,
      allowEditing: false,
      resultType: CameraResultType.Base64,
      source: CameraSource.Camera, // Camera only — no gallery.
      direction: facing === 'user' ? CameraDirection.Front : CameraDirection.Rear,
    });
    const base64 = photo.base64String ?? '';
    const bin = atob(base64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
    return new Blob([bytes], { type: 'image/jpeg' });
  }
  return captureWeb(facing);
}

async function captureWeb(facing: FacingMode): Promise<Blob> {
  const stream = await navigator.mediaDevices.getUserMedia({
    video: { facingMode: facing },
  });
  try {
    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    await video.play();
    // Give the camera one frame to stabilize before snapping.
    await new Promise((r) => setTimeout(r, 250));
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')?.drawImage(video, 0, 0);
    const blob = await new Promise<Blob | null>((res) =>
      canvas.toBlob((b) => res(b), 'image/jpeg', 0.85),
    );
    if (!blob) throw new Error('Capture failed');
    return blob;
  } finally {
    for (const track of stream.getTracks()) track.stop();
  }
}
